import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, Wifi } from "lucide-react";
import { EASE } from "./Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?q=85&w=1200&auto=format&fit=crop";

const MaskedLine = ({ children, delay }) => (
  <span className="mask-line">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ onNavigate }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex flex-col justify-center pt-24 pb-16" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
            className="text-xs uppercase tracking-[0.35em] text-gold font-medium mb-8"
            data-testid="hero-eyebrow"
          >
            French lessons · Houston area · Online
          </motion.p>
          <h1 className="font-serif leading-[0.95] text-6xl sm:text-7xl lg:text-8xl" data-testid="hero-title">
            <MaskedLine delay={0.2}>The French</MaskedLine>
            <MaskedLine delay={0.35}>Atelier</MaskedLine>
            <MaskedLine delay={0.5}>
              <span className="italic text-gold">by Cécile.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.75 }}
            className="mt-8 max-w-md text-base sm:text-lg text-navy/70 leading-relaxed"
            data-testid="hero-subtitle"
          >
            Private French lessons for children, teens, adults and
            professionals — with a native Parisian teacher who has taught
            for over fifteen years.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => onNavigate("#contact")}
              className="btn-fill border border-navy px-8 py-4 text-sm tracking-wide"
              data-testid="hero-cta-book"
            >
              Book your first lesson
            </button>
            <button
              onClick={() => onNavigate("#services")}
              className="link-underline text-sm tracking-wide inline-flex items-center gap-2"
              data-testid="hero-cta-discover"
            >
              Explore lessons <ArrowDown size={16} />
            </button>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: EASE, delay: 0.55 }}
            className="relative"
          >
            <div className="absolute -top-4 -right-4 w-full h-full border border-gold pointer-events-none" aria-hidden="true" />
            <div className="relative overflow-hidden aspect-[3/4]" data-testid="hero-image-frame">
              <motion.img
                src={HERO_IMG}
                alt="The Eiffel Tower in Paris, France"
                style={{ y: imgY }}
                initial={{ scale: 1.15 }}
                animate={{ scale: 1.08 }}
                transition={{ duration: 1.6, ease: EASE }}
                className="w-full h-[120%] object-cover object-top"
                data-testid="hero-image"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 1.1 }}
              className="absolute -bottom-6 -left-6 bg-cream border border-sand px-6 py-4 shadow-[0_24px_60px_-30px_rgba(17,28,56,0.35)]"
              data-testid="hero-caption-card"
            >
              <p className="font-serif italic text-lg leading-snug">Native Parisian</p>
              <p className="text-xs uppercase tracking-[0.25em] text-navy/60 mt-1">15+ years of teaching</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="max-w-7xl mx-auto px-6 lg:px-12 w-full mt-16 flex flex-wrap gap-x-10 gap-y-3 text-xs uppercase tracking-[0.25em] text-navy/50"
        data-testid="hero-badges"
      >
        <span className="inline-flex items-center gap-2"><MapPin size={14} className="text-gold" /> Houston area — Katy, Cinco Ranch, Fulshear, Richmond</span>
        <span className="inline-flex items-center gap-2"><Wifi size={14} className="text-gold" /> Online, anywhere in the world</span>
      </motion.div>
    </section>
  );
}
